import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import GeoLibreFrame from "../components/geolibre/GeoLibreFrame";
import { applyMissingDataStyle } from "../components/geolibre/geolibreStyleUtils";
import {
  activeGeoLibreLegends,
  buildGeoLibreProject,
  hydrateGeoLibreVectorLayer,
  sanitizeGeoLibreProjectPlugins,
} from "../components/geolibre/geolibreProject";
import LandingNavbar from "../components/landing_navbar";
import {
  blockAtom,
  districtAtom,
  stateAtom,
} from "../store/locationStore";
import {
  initializeAnalytics,
  trackPageView,
} from "../services/analytics";

const labelOf = (selection) => selection?.label || "";

const scopeKeyOf = (project) => {
  const scope = project?.metadata?.scope;
  return scope ? [scope.state, scope.district, scope.tehsil].join("|") : "";
};

const mergeHydratedVectorLayers = (viewerProject, hydratedLayers) => ({
  ...viewerProject,
  layers: viewerProject.layers.map((layer) => {
    const hydrated = hydratedLayers.get(layer.id);
    if (!hydrated) return layer;
    return {
      ...layer,
      geojson: hydrated.geojson,
      popup: hydrated.popup,
      style: layer.metadata?.loadState !== "loaded" && !layer.style?.vectorStyleStops?.length
        ? hydrated.style : layer.style,
      metadata: {
        ...layer.metadata,
        ...hydrated.metadata,
        corestack: {
          ...layer.metadata?.corestack,
          ...hydrated.metadata?.corestack,
        },
      },
    };
  }),
});

const rememberHydratedVectorLayers = (project, hydratedLayers) => {
  project.layers.forEach((layer) => {
    if (
      layer.type === "geojson" &&
      layer.metadata?.loadState === "loaded"
    ) {
      hydratedLayers.set(layer.id, layer);
    }
  });
};

const LandscapeExplorer = () => {
  const selectedState = useRecoilValue(stateAtom);
  const selectedDistrict = useRecoilValue(districtAtom);
  const selectedTehsil = useRecoilValue(blockAtom);
  const routeLocation = useLocation();
  const [project, setProject] = useState(null);
  const [legends, setLegends] = useState([]);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const currentScopeKeyRef = useRef("");
  const lazyQueueRef = useRef(Promise.resolve());
  const lazyStateSequenceRef = useRef(0);
  const hydratedLayersRef = useRef(new Map());
  const hydrationDirtyRef = useRef(false);
  const lazyAbortRef = useRef(null);

  const scope = useMemo(() => {
    const params = new URLSearchParams(routeLocation.search);
    return {
      state: params.get("state") || labelOf(selectedState),
      district: params.get("district") || labelOf(selectedDistrict),
      tehsil: params.get("tehsil") || labelOf(selectedTehsil),
    };
  }, [
    routeLocation.search,
    selectedDistrict,
    selectedState,
    selectedTehsil,
  ]);

  const hasLocation = Boolean(scope.state && scope.district && scope.tehsil);
  const scopeKey = [scope.state, scope.district, scope.tehsil].join("|");

  useEffect(() => {
    lazyAbortRef.current?.abort();
    lazyAbortRef.current = new AbortController();
    currentScopeKeyRef.current = scopeKey;
    lazyStateSequenceRef.current += 1;
    lazyQueueRef.current = Promise.resolve();
    hydratedLayersRef.current = new Map();
    hydrationDirtyRef.current = false;
    setLegends([]);
    return () => lazyAbortRef.current?.abort();
  }, [scopeKey, retryKey]);

  useEffect(() => {
    initializeAnalytics();
    trackPageView("/explore_data");
  }, []);

  useEffect(() => {
    if (!hasLocation) return undefined;
    const controller = new AbortController();
    setProject(null);
    setError("");

    buildGeoLibreProject({
      ...scope,
      signal: controller.signal,
      viewport: {
        width: Math.max(window.innerWidth - 340, 320),
        height: Math.max(window.innerHeight - 100, 320),
      },
    })
      .then((nextProject) => {
        if (controller.signal.aborted) return;
        setProject(nextProject);
        setLegends(activeGeoLibreLegends(nextProject));
      })
      .catch((buildError) => {
        if (controller.signal.aborted) return;
        setError(
          buildError instanceof Error
            ? buildError.message
            : "The tehsil project could not be generated."
        );
      });

    return () => controller.abort();
  }, [hasLocation, retryKey, scope]);

  const handleProjectState = useCallback((viewerProject) => {
    const viewerScopeKey = scopeKeyOf(viewerProject);
    if (viewerScopeKey !== currentScopeKeyRef.current) return;
    if (viewerScopeKey === currentScopeKeyRef.current) {
      setLegends(activeGeoLibreLegends(viewerProject));
    }
    const sequence = lazyStateSequenceRef.current + 1;
    lazyStateSequenceRef.current = sequence;

    lazyQueueRef.current = lazyQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const controller = lazyAbortRef.current;
        if (viewerScopeKey !== currentScopeKeyRef.current || controller?.signal.aborted) return;

        const mergedProject = mergeHydratedVectorLayers(
          viewerProject,
          hydratedLayersRef.current
        );
        let nextProject = mergedProject;
        const styledLayers = nextProject.layers.map(applyMissingDataStyle);
        const missingStyleChanged = styledLayers.some((layer, index) => layer !== nextProject.layers[index]);
        if (missingStyleChanged) nextProject = { ...nextProject, layers: styledLayers };
        const layersToLoad = nextProject.layers.filter(
          (layer) =>
            layer.type === "geojson" &&
            layer.visible &&
            ["unloaded", "error"].includes(layer.metadata?.loadState)
        );

        for (const layer of layersToLoad) {
          nextProject = await hydrateGeoLibreVectorLayer({
            project: nextProject,
            layerId: layer.id,
            signal: controller?.signal,
          });
          if (controller?.signal.aborted) return;
          // One NREGA request hydrates every independently toggleable work-type
          // layer. Remember every hydrated sibling so a later visibility event
          // cannot merge against stale empty data.
          rememberHydratedVectorLayers(
            nextProject,
            hydratedLayersRef.current
          );
          hydrationDirtyRef.current = true;
        }

        if (
          viewerScopeKey !== currentScopeKeyRef.current ||
          sequence !== lazyStateSequenceRef.current ||
          (!layersToLoad.length && !hydrationDirtyRef.current && !missingStyleChanged)
        ) {
          return;
        }

        // A visibility-only state already lives inside the GeoLibre iframe.
        // Sending it back as a full project would recreate every native raster
        // source and make an already-loaded WMS layer fetch its tiles again.
        // Only replace the project when lazy vector hydration supplied new data.
        nextProject = sanitizeGeoLibreProjectPlugins(nextProject);
        nextProject.styles = Object.fromEntries(nextProject.layers.map(layer => [layer.id, layer.style]));
        rememberHydratedVectorLayers(nextProject, hydratedLayersRef.current);
        hydrationDirtyRef.current = false;
        setProject(nextProject);
      }).catch(loadError => {
        if (loadError?.name !== "AbortError" && viewerScopeKey === currentScopeKeyRef.current) {
          setError(loadError instanceof Error ? loadError.message : "A layer could not be prepared.");
        }
      });
  }, []);

  if (!hasLocation) {
    return (
      <div className="flex h-screen flex-col bg-slate-100">
        <LandingNavbar downloadScope={scope} />
        <main className="flex min-h-0 flex-1 items-center justify-center p-6">
          <div className="max-w-md rounded-2xl bg-white p-7 text-center shadow-xl">
            <h1 className="text-xl font-semibold text-slate-900">
              Select a tehsil first
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              GeoLibre projects are generated for a selected state, district,
              and tehsil.
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex rounded-lg bg-purple-700 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-800"
            >
              Select location
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const failures =
    project?.metadata?.layerLoading?.initialLoadFailures?.length || 0;
  const lazyFailures =
    project?.metadata?.layerLoading?.lazyLoadFailures?.length || 0;
  const totalFailures = failures + lazyFailures;
  const warning = totalFailures
    ? `${totalFailures} layer${totalFailures === 1 ? "" : "s"} could not be loaded. Toggle the layer off and on to retry.`
    : "";

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white">
      <LandingNavbar downloadScope={scope} />
      <GeoLibreFrame
        key={`${scopeKey}:${retryKey}`}
        project={project}
        preparationError={error}
        warning={warning}
        legends={legends}
        onProjectState={handleProjectState}
        onRetry={() => setRetryKey((value) => value + 1)}
      />
    </div>
  );
};

export default LandscapeExplorer;
